/**
 * Ally and enemy decisions, from the wiki page Architecture → game-core
 * contract, section AI. Only round.ts calls this. Rolls use the `ai` stream,
 * so a choice returns the state with that stream advanced. Ties break by
 * unit id (the order of `state.units`), then by direction order.
 */
import type { Skill } from '@ab/content-schema';
import { checkSkillUse, maxHp, skillUsability, typePercent } from './combat.ts';
import type { Ctx } from './commands/types.ts';
import { lookup } from './content.ts';
import { idiv } from './determinism.ts';
import { gridView } from './floorview.ts';
import {
  type Dir,
  type GridView,
  type Pos,
  type Walk,
  type WalkTable,
  canStep,
  chebyshev,
  cornerClear,
  dirTo,
  dirs,
  manhattan,
  reachableWithin,
  samePos,
  stepIn,
  walkIn,
  walkTable,
} from './grid.ts';
import { nextInt, roll } from './rng.ts';
import { InvalidCommand, type RunState, type Unit, compareUnitIds, leaderOf, maySwapWith, unitById, withRng } from './state.ts';
import { typesOf } from './stats.ts';

export type AiAction =
  | { readonly kind: 'attack'; readonly dir: Dir }
  | { readonly kind: 'move'; readonly dir: Dir }
  | { readonly kind: 'wait' }
  /** Face `dir` and use `skill` (combat.ts `useSkill`); `target` for shape `single`. */
  | { readonly kind: 'skill'; readonly skill: string; readonly dir: Dir; readonly target: string | null };

export type AiChoice = { readonly action: AiAction; readonly state: RunState };

const wait: AiAction = { kind: 'wait' };

/** `hp × 100 ÷ max(1, maxHp)`, integer division: how hurt a unit is, for the AI's choices. */
const hpPercent = (ctx: Ctx, o: Unit): number => idiv(o.hp * 100, Math.max(1, maxHp(ctx, o)));

/**
 * Whether `a` is a better target than `b` at the same distance: the lower
 * `hpPercent`, then the lower unit id. Callers walk `state.units` in id order,
 * so "not better" keeps the earlier id.
 */
const moreHurt = (ctx: Ctx, a: Unit, b: Unit): boolean => hpPercent(ctx, a) < hpPercent(ctx, b);

/**
 * The standing unit of `side` that `u` could hit with a basic attack:
 * adjacent (8 neighbours), and for a diagonal neighbour only when the corner
 * rule allows the attack (grid.ts `cornerClear`). Among several, the lowest
 * `hpPercent`, ties by unit id.
 */
const adjacentOf = (ctx: Ctx, state: RunState, u: Unit, side: Unit['side']): Unit | null => {
  const g = gridView(state);
  let best: Unit | null = null;
  for (const o of state.units) {
    if (o.side !== side || o.down || chebyshev(o.pos, u.pos) !== 1) continue;
    const d = dirTo(u.pos, o.pos);
    if (d === null || !cornerClear(g, u.pos, d)) continue;
    if (best === null || moreHurt(ctx, o, best)) best = o;
  }
  return best;
};

/**
 * A skill's type multiplier against `foe`, for the AI's choice: the type
 * chart percent (combat.ts `typePercent`) of its first `damage` effect's
 * element, or the skill's element when that effect names none, against the
 * foe's types. A skill with no `damage` effect counts 100.
 */
const skillTypePercent = (ctx: Ctx, skill: Skill, foe: Unit): number => {
  const damage = skill.effects.find((e) => e.type === 'damage');
  if (damage === undefined || damage.type !== 'damage') return 100;
  return typePercent(ctx, damage.element ?? skill.element ?? null, typesOf(ctx.content, foe));
};

/**
 * The skill the unit would use on the adjacent `foe` facing `dir`: among the
 * skills that pass `checkSkillUse` (known, runnable, affordable, a heal has
 * someone to heal, not blocked by disrupt) and whose targeting, with `foe` as
 * the target of a `single` shape, hits `foe` itself (not just any unit of its side),
 * the one with the highest `skillTypePercent` against `foe`; skills below 100
 * are skipped; ties by the unit's skill order. Null if none.
 */
const usableSkill = (ctx: Ctx, state: RunState, u: Unit, foe: Unit, dir: Dir): { skill: string; target: string | null } | null => {
  let best: { skill: string; target: string | null; pct: number } | null = null;
  for (const skillId of u.skills) {
    const skill = lookup(ctx.content.skills, skillId);
    if (skill === null) continue;
    const pct = skillTypePercent(ctx, skill, foe);
    if (pct < 100 || (best !== null && pct <= best.pct)) continue;
    const target = skill.targeting.shape === 'single' ? foe.id : null;
    try {
      const plan = checkSkillUse(ctx, state, u.id, skillId, dir, target, false);
      if (plan.targets.some((t) => t.unit === foe.id)) best = { skill: skillId, target, pct };
    } catch (e) {
      if (!(e instanceof InvalidCommand)) throw e;
    }
  }
  return best === null ? null : { skill: best.skill, target: best.target };
};

/**
 * Face an adjacent foe and attack it. When the unit has a usable skill that
 * hits a foe from where it stands (`usableSkill`: the best type multiplier
 * against that foe, never a resisted one), roll
 * `dungeon.enemySkillChance` on the `ai` stream (only then, so a unit
 * without one draws nothing) and use that skill on a hit; else basic attack.
 */
const attackFoe = (ctx: Ctx, state: RunState, u: Unit, foe: Unit): AiChoice => {
  const dir = dirTo(u.pos, foe.pos);
  if (dir === null) return { action: wait, state };
  const basic: AiAction = { kind: 'attack', dir };
  const pick = usableSkill(ctx, state, u, foe, dir);
  if (pick === null) return { action: basic, state };
  const r = roll(state.rng.ai, ctx.content.rules.dungeon.enemySkillChance);
  const s = withRng(state, 'ai', r.next);
  return { action: r.hit ? { kind: 'skill', skill: pick.skill, dir, target: pick.target } : basic, state: s };
};

/**
 * The standing party units the enemy notices (same room, or within sight
 * range), nearest first by Manhattan distance, then the lowest `hpPercent`,
 * then unit id.
 */
const noticedTargets = (ctx: Ctx, state: RunState, g: GridView, u: Unit): Unit[] => {
  const room = g.roomAt(u.pos);
  const seen = state.units.filter((o) => {
    if (o.side !== 'party' || o.down) return false;
    const sameRoom = room !== null && g.roomAt(o.pos) === room;
    return sameRoom || manhattan(u.pos, o.pos) <= ctx.content.rules.dungeon.enemySightRange;
  });
  return seen.sort(
    (a, b) => manhattan(u.pos, a.pos) - manhattan(u.pos, b.pos) || hpPercent(ctx, a) - hpPercent(ctx, b) || compareUnitIds(a.id, b.id),
  );
};

/** With `enemyWanderChance`%, one step in a random free direction; else wait. */
const wander = (ctx: Ctx, state: RunState, g: GridView, u: Unit): AiChoice => {
  const r = roll(state.rng.ai, ctx.content.rules.dungeon.enemyWanderChance);
  let s = withRng(state, 'ai', r.next);
  if (!r.hit) return { action: wait, state: s };
  const open = dirs.filter((d) => canStep(g, u.pos, d));
  if (open.length === 0) return { action: wait, state: s };
  const pick = nextInt(s.rng.ai, open.length);
  s = withRng(s, 'ai', pick.next);
  const dir = open[pick.value];
  return { action: dir === undefined ? wait : { kind: 'move', dir }, state: s };
};

/**
 * The standing unit of `u`'s side (itself included) with the lowest
 * `hp × 100 ÷ max(1, maxHp)`, when that is strictly below
 * `dungeon.aiHealBelowPercent`; ties by unit id. Null when none is.
 */
const mostHurt = (ctx: Ctx, state: RunState, u: Unit): Unit | null => {
  const pct = (o: Unit): number => hpPercent(ctx, o);
  let best: Unit | null = null;
  for (const o of state.units) {
    if (o.side !== u.side || o.down || pct(o) >= ctx.content.rules.dungeon.aiHealBelowPercent) continue;
    if (best === null || pct(o) < pct(best)) best = o;
  }
  return best;
};

/** A skill heals when one of its effects is `heal`, and may reach an ally: shape `self` (the user only), or `who` allies or everyone. */
const healsAllies = (skill: Skill): boolean =>
  skill.effects.some((e) => e.type === 'heal') && (skill.targeting.shape === 'self' || skill.targeting.who === 'allies' || skill.targeting.who === 'everyone');

/**
 * The heal branch: when a unit of its side is hurt (`mostHurt`), the first
 * skill in the unit's list that heals (`healsAllies`), passes `checkSkillUse`
 * with the unit's current facing (and the hurt unit as the target of a
 * `single` shape) and whose resolved targets include the hurt unit. No
 * stream draw. Null when there is no such unit or skill.
 */
const healChoice = (ctx: Ctx, state: RunState, u: Unit): AiAction | null => {
  const patient = mostHurt(ctx, state, u);
  if (patient === null) return null;
  for (const skillId of u.skills) {
    const skill = lookup(ctx.content.skills, skillId);
    if (skill === null || !healsAllies(skill)) continue;
    const target = skill.targeting.shape === 'single' ? patient.id : null;
    try {
      const plan = checkSkillUse(ctx, state, u.id, skillId, u.facing, target, false);
      if (plan.targets.some((t) => t.unit === patient.id)) return { kind: 'skill', skill: skillId, dir: u.facing, target };
    } catch (e) {
      if (!(e instanceof InvalidCommand)) throw e;
    }
  }
  return null;
};

/**
 * One decision's walk tables (grid.ts `walkTable`), each searched at most
 * once and only when a rule asks for it: `table` over `grid` (the grid the
 * unit steps on: `swapGrid` for an ally, the plain grid for an enemy),
 * `plain` over the plain grid (an ally's walks without swaps), `through` over
 * `grid` with the unit's own side counted free (`queuedStep`). A depth bounds
 * the search (engage); an unbounded table answers bounded questions too.
 */
type Walks = {
  readonly state: RunState;
  readonly u: Unit;
  readonly grid: GridView;
  readonly table: (depth: number | null) => WalkTable;
  readonly plain: (depth: number | null) => WalkTable;
  readonly through: (depth: number | null) => WalkTable;
};

/** A memo of one table per depth, preferring an unbounded one already searched. */
const tableCache = (make: (depth: number | null) => WalkTable): ((depth: number | null) => WalkTable) => {
  let full: WalkTable | null = null;
  let bounded: { readonly depth: number; readonly table: WalkTable } | null = null;
  return (depth) => {
    if (full !== null) return full;
    if (depth === null) {
      full = make(null);
      return full;
    }
    if (bounded === null || bounded.depth !== depth) bounded = { depth, table: make(depth) };
    return bounded.table;
  };
};

const walksOf = (state: RunState, u: Unit, grid: GridView): Walks => {
  const throughGrid: GridView = {
    ...grid,
    unitAt: (p) => {
      const id = grid.unitAt(p);
      return id !== null && id !== u.id && unitById(state, id).side === u.side ? null : id;
    },
  };
  const table = tableCache((depth) => walkTable(grid, u.pos, depth));
  const plainGrid = u.side === 'enemy' ? null : gridView(state);
  return {
    state,
    u,
    grid,
    table,
    plain: plainGrid === null ? table : tableCache((depth) => walkTable(plainGrid, u.pos, depth)),
    through: tableCache((depth) => walkTable(throughGrid, u.pos, depth)),
  };
};

/**
 * A step toward `to` over the unit's grid: the first step of a shortest walk
 * (grid.ts `shortestWalk`, read from the decision's walk table). When units of
 * the unit's own side block every walk (a queue in a corridor), the first step
 * of a shortest walk that passes through them instead, if that step is free
 * now, so the unit closes up behind them rather than standing still; they move
 * on in their own turns. Null otherwise.
 */
const queuedStep = (w: Walks, to: Pos, depth: number | null): Dir | null => {
  const walk = walkIn(w.table(depth), to);
  if (walk !== null) return walk.first;
  const queued = walkIn(w.through(depth), to);
  return queued !== null && canStep(w.grid, w.u.pos, queued.first) ? queued.first : null;
};

/**
 * An enemy's choice: the heal branch; attack an adjacent party unit; else,
 * when it notices party units (`noticedTargets`), a step toward the first of
 * them it can walk to (`queuedStep`), or a wait when it can walk to none (a
 * target walled off or boxed in by its allies gives way to the next one);
 * else wander.
 */
const enemyChoice = (ctx: Ctx, state: RunState, u: Unit): AiChoice => {
  const heal = healChoice(ctx, state, u);
  if (heal !== null) return { action: heal, state };
  const foe = adjacentOf(ctx, state, u, 'party');
  if (foe !== null) return attackFoe(ctx, state, u, foe);
  const g = gridView(state);
  const targets = noticedTargets(ctx, state, g, u);
  if (targets.length === 0) return wander(ctx, state, g, u);
  const w = walksOf(state, u, g);
  for (const target of targets) {
    const dir = queuedStep(w, target.pos, null);
    if (dir !== null) return { action: { kind: 'move', dir }, state };
  }
  return { action: wait, state };
};

/**
 * The grid an ally may swap over: a tile holding a party unit the ally may
 * swap with (state.ts `maySwapWith`: an ally earlier in party order, never
 * the leader, never a charging one) counts as free, since round.ts `moveUnit` swaps the two.
 */
const swapGrid = (state: RunState, u: Unit): GridView => {
  const g = gridView(state);
  return {
    ...g,
    unitAt: (p) => {
      const id = g.unitAt(p);
      if (id === null || id === u.id) return id;
      return unitById(state, id).side === 'party' && maySwapWith(state, u.id, id) ? null : id;
    },
  };
};

/** Of two walks to the same tile, the one without swaps when it is just as short, so an ally does not push others around needlessly. */
const preferFree = (swapping: Walk, free: Walk | null): AiAction => ({
  kind: 'move',
  dir: free !== null && free.length === swapping.length ? free.first : swapping.first,
});

/**
 * The first step of a shortest walk to `to` over the grid where allies the
 * unit may swap with count as free (`swapGrid`); when a walk without swaps is
 * just as short, its first step instead (`preferFree`). When allies block
 * every walk, the step that closes up behind them (`queuedStep`). Null when
 * there is no such step, or the unit already stands one step from `to`. A
 * `depth` bounds the searches; pass one only for a `to` known to be within it.
 */
const allyStep = (w: Walks, to: Pos, depth: number | null): AiAction | null => {
  const swapping = walkIn(w.table(depth), to);
  if (swapping === null) {
    const dir = queuedStep(w, to, depth);
    return dir === null ? null : { kind: 'move', dir };
  }
  return preferFree(swapping, walkIn(w.plain(depth), to));
};

/**
 * A healer walks until its heal reaches the patient. When another unit of its
 * side is hurt (`mostHurt`) and the unit knows a skill that heals allies other
 * than itself (`healsAllies` with a shape other than `self`) and could use it
 * now ignoring targeting (`skillUsability`): when it already stands where a
 * `single` one reaches the patient (`reachableWithin` the skill's range, the
 * rule of `single` targeting), null, and the heal branch acts when it can;
 * else a step toward the patient (`allyStep`); when that gives none (the
 * patient one step away, a diagonal neighbour of a range-1 healer, or boxed
 * in) the first step of a shortest walk to the nearest free tile from which a
 * `single` one would reach (fewest steps, then walk order). Null otherwise.
 */
const towardPatient = (ctx: Ctx, w: Walks): AiAction | null => {
  const { state, u } = w;
  const patient = mostHurt(ctx, state, u);
  if (patient === null || patient.id === u.id) return null;
  const heals: Skill[] = [];
  for (const id of u.skills) {
    const skill = lookup(ctx.content.skills, id);
    // A `self` heal cannot reach the patient, so it is no reason to walk to it.
    if (skill !== null && healsAllies(skill) && skill.targeting.shape !== 'self' && skillUsability(ctx.content, state, u.id, id) === null) heals.push(skill);
  }
  if (heals.length === 0) return null;
  // The tiles from which a `single` heal reaches the patient; reach is symmetric (orthogonal steps over floor, units ignored).
  const plain = gridView(state);
  const zone = heals.flatMap((s) => (s.targeting.shape === 'single' ? reachableWithin(plain, patient.pos, s.targeting.range ?? 1) : []));
  const inZone = (p: Pos): boolean => zone.some((q) => samePos(q, p));
  if (inZone(u.pos)) return null;
  const step = allyStep(w, patient.pos, null);
  if (step !== null) return step;
  const table = w.table(null);
  const goal = table.order.find((p) => inZone(p) && w.grid.unitAt(p) === null);
  const swapping = goal === undefined ? null : stepIn(table, goal);
  return goal === undefined || swapping === null ? null : preferFree(swapping, stepIn(w.plain(null), goal));
};

/**
 * The standing enemy with the shortest walk from the ally (grid.ts
 * `shortestWalk` over `swapGrid`), when that walk is at most
 * `dungeon.allyEngageRange` steps; ties by unit id. Then a step toward it
 * (`allyStep`). Null when there is none. One search bounded by the range
 * (grid.ts `walkTable`) answers every enemy. Measuring the range in steps rather
 * than Manhattan distance keeps an ally from bouncing: every step of the walk
 * shortens it, so an enemy that stands still stays in range, and an enemy only
 * reachable by a long detour (a corridor the leader blocks) is not engaged.
 */
const engage = (ctx: Ctx, w: Walks): AiAction | null => {
  const range = ctx.content.rules.dungeon.allyEngageRange;
  const table = w.table(range);
  let best: { unit: Unit; length: number } | null = null;
  for (const o of w.state.units) {
    if (o.side !== 'enemy' || o.down) continue;
    const walk = walkIn(table, o.pos);
    if (walk === null || walk.length > range) continue;
    if (best === null || walk.length < best.length) best = { unit: o, length: walk.length };
  }
  return best === null ? null : allyStep(w, best.unit.pos, range);
};

/**
 * An ally's choice, the first that applies: the heal branch; attack an
 * adjacent foe; a healer walks until its heal reaches its patient
 * (`towardPatient`); step toward the enemy with the shortest walk of at most
 * `allyEngageRange` steps (`engage`); step toward the leader when farther than 1 (Chebyshev); wait.
 * Steps follow a shortest walk, swapping with an ally in the way (`allyStep`);
 * a rule whose target cannot be walked to gives way to the next one.
 */
const allyChoice = (ctx: Ctx, state: RunState, u: Unit): AiChoice => {
  const heal = healChoice(ctx, state, u);
  if (heal !== null) return { action: heal, state };
  const foe = adjacentOf(ctx, state, u, 'enemy');
  if (foe !== null) return attackFoe(ctx, state, u, foe);
  const w = walksOf(state, u, swapGrid(state, u));
  const move = towardPatient(ctx, w) ?? engage(ctx, w);
  if (move !== null) return { action: move, state };
  const leader = leaderOf(state);
  if (leader.id === u.id || leader.down || chebyshev(u.pos, leader.pos) <= 1) return { action: wait, state };
  return { action: allyStep(w, leader.pos, null) ?? wait, state };
};


/** What a non-leader unit does this round. */
export const chooseAction = (ctx: Ctx, state: RunState, unitId: string): AiChoice => {
  const u = unitById(state, unitId);
  return u.side === 'enemy' ? enemyChoice(ctx, state, u) : allyChoice(ctx, state, u);
};
